import type { PayloadCipherContext } from './IEncryptor';

export interface ISyncEncryptor {
  decrypt(salt: string, encoded: string): string;
  decryptPayload<T = object>(hmac: string, context: PayloadCipherContext, encoded: string): T;
  encrypt(salt: string, plaintext: string): string;
  encryptPayload<T = object>(hmac: string, context: PayloadCipherContext, unencoded: T): string;
}

/**
 * WeakEncryptor: XOR-based cipher for encryption (NOT cryptographically secure).
 * WARNING: This encryption method is trivially breakable and provides no real security.
 * It uses a simple XOR cipher that can be broken using frequency analysis and
 * known-plaintext attacks. This should ONLY be used for:
 * - Non-sensitive data obfuscation
 * - Testing purposes
 * For production use with sensitive data, use StrongEncryptor instead.
 */
export const WeakEncryptor: ISyncEncryptor = {
  /**
   * Decrypts a string using XOR cipher.
   * See: https://stackoverflow.com/a/66938952/886279
   * @param salt - The secret key required to decrypt.
   * @param encoded - The encoded String.
   */
  decrypt(salt: string, encoded: string): string {
    if (!encoded) return '';
    /** Pre-compute salt XOR value once */
    let saltXor = 0;
    for (let i = 0; i < salt.length; i++) {
      saltXor ^= salt.charCodeAt(i);
    }
    /** Single-pass through encoded string (2 hex chars at a time) */
    let result = '';
    for (let i = 0; i < encoded.length; i += 2) {
      const code = parseInt(encoded.slice(i, i + 2), 16) ^ saltXor;
      result += String.fromCharCode(code);
    }
    return result;
  },
  /**
   * Decrypts a payload. Accepts encrypted string.
   * @param hmac - The HMAC secret required to decrypt.
   * @param context - The context (request or response) for key derivation.
   * @param encoded - The encrypted string you want to decode.
   */
  decryptPayload<T = object>(hmac: string, context: PayloadCipherContext, encoded: string): T {
    try {
      const derivedKey = deriveKey(hmac, context);
      const decrypted = WeakEncryptor.decrypt(derivedKey, encoded);
      return JSON.parse(decrypted) as T;
    } catch {
      throw new Error('Failed to decrypt payload: invalid format or corrupted data');
    }
  },
  /**
   * Encrypts a string using XOR cipher.
   * See: https://stackoverflow.com/a/66938952/886279
   * @param salt - The secret key required to encrypt.
   * @param plaintext - The plaintext String you want to encode.
   */
  encrypt(salt: string, plaintext: string): string {
    /** Pre-compute salt XOR value once */
    let saltXor = 0;
    for (let i = 0; i < salt.length; i++) {
      saltXor ^= salt.charCodeAt(i);
    }
    /** Single-pass through plaintext */
    let result = '';
    for (let i = 0; i < plaintext.length; i++) {
      const code = plaintext.charCodeAt(i) ^ saltXor;
      result += ('0' + code.toString(16)).slice(-2);
    }
    return result;
  },
  /**
   * Encrypts a payload. Returns encrypted string.
   * @template T
   * @param hmac - The HMAC secret required to encrypt.
   * @param context - The context (request or response) for key derivation.
   * @param unencoded - The object you want to encode.
   */
  encryptPayload<T = object>(hmac: string, context: PayloadCipherContext, unencoded: T): string {
    const jsonString = JSON.stringify(unencoded);
    const derivedKey = deriveKey(hmac, context);
    return WeakEncryptor.encrypt(derivedKey, jsonString);
  }
};

/**
 * Derives an encryption key from the HMAC secret.
 * Uses different contexts to ensure separate keys for request/response.
 * @param hmacSecret - The HMAC secret to derive the key from.
 * @param context - The context (request or response) for key derivation.
 * @returns A derived key string for use with WeakEncryptor
 */
const deriveKey = (hmacSecret: string, context: PayloadCipherContext): string => {
  return hmacSecret + ':' + context;
};
